import { Stack, Typography, Box } from "@mui/material";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Link } from "react-router-dom";
import { IconChevronRight } from "@/assets/icons";
import { text, background, divider, black, blue } from "@/theme/colors";
import { spacing, rounded } from "@/theme/variables";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { useVocabularyCategories, TVocabularyCategory } from "@/infrastructure/adapters/vocabulary";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { AppRoutes } from "@/routes/config";
import { buildCategoryRoute } from "@/utils/url";

export const VocabularyCategoryListPage: FC = () => {
  const { t } = useTranslation();
  const { isTrial } = useTrialStatus();
  const { categories, isLoading } = useVocabularyCategories({ enabled: !isTrial });

  if (isTrial) {
    return <Navigate to={AppRoutes.study} replace />;
  }

  if (isLoading) {
    return <LoadingArea />;
  }

  if (categories === null) {
    return <Navigate to={buildCategoryRoute(AppRoutes.vocabularyDetail)} replace />;
  }

  if (categories.length === 1) {
    return <Navigate to={buildCategoryRoute(AppRoutes.vocabularyDetail, categories[0].category)} replace />;
  }

  return (
    <Stack sx={{ gap: spacing.sm }}>
      <Typography variant="sub16Bold" color={text.primary}>
        {t("vocabulary.categoryListTitle")}
      </Typography>

      <Stack gap={2}>
        {categories.map((category) => (
          <VocabularyCategoryCard key={category.category} category={category} />
        ))}
      </Stack>
    </Stack>
  );
};

type VocabularyCategoryCardProps = {
  category: TVocabularyCategory;
};

const VocabularyCategoryCard: FC<VocabularyCategoryCardProps> = ({ category }) => {
  const { t } = useTranslation();

  return (
    <Stack
      direction="row"
      component={Link}
      to={buildCategoryRoute(AppRoutes.vocabularyDetail, category.category)}
      alignItems="center"
      justifyContent="space-between"
      p={spacing.smd}
      bgcolor={background.white}
      border={`1px solid ${divider.high}`}
      borderRadius={rounded.xs}
    >
      <Stack gap={0.5}>
        <Typography variant="body14Semi" color={text.primary}>
          {category.category}
        </Typography>
        <Stack direction="row" gap={0.5} alignItems="center">
          <Typography variant="cap10Light" color={text.secondary} fontWeight={500}>
            {t("vocabulary.learnedWordsPrefix")}
          </Typography>
          <Typography variant="cap10Light" color={blue[80]} fontWeight={500}>
            {t("vocabulary.learnedWordsCount", { count: category.learnedCount })}
          </Typography>
        </Stack>
      </Stack>
      <Box display="flex" fontSize={24} flexShrink={0}>
        <IconChevronRight color={black[90]} />
      </Box>
    </Stack>
  );
};

export default VocabularyCategoryListPage;
